import {
  CATALOG_TAGS_INLINE_GAP_PX,
  CATALOG_TAGS_INLINE_MIN_LAST_TAG_MAX_WIDTH_PX,
} from './catalogTagsInlineConstants';

export type CatalogTagsInlineFitResult = {
  visibleCount: number;
  /** Число тегов на первой строке (остальные — на второй). */
  row1Count: number;
  showMore: boolean;
  hiddenCount: number;
  /** max-width последнего тега на второй строке перед «и ещё». */
  lastTagMaxWidth: number | undefined;
};

function segmentWidth(
  naturalWidths: number[],
  from: number,
  to: number,
  lastTagMaxWidth?: number
): number {
  if (to <= from) return 0;
  let sum = 0;
  for (let i = from; i < to; i += 1) {
    const w = naturalWidths[i] ?? 0;
    const isLast = i === to - 1;
    if (isLast && lastTagMaxWidth != null) {
      sum += Math.min(w, lastTagMaxWidth);
    } else {
      sum += w;
    }
  }
  const gapCount = to - from - 1;
  return sum + CATALOG_TAGS_INLINE_GAP_PX * gapCount;
}

function row2WidthWithMore(
  naturalWidths: number[],
  row2From: number,
  row2To: number,
  lastTagMaxWidth: number | undefined,
  moreWidth: number
): number {
  const tagsWidth = segmentWidth(naturalWidths, row2From, row2To, lastTagMaxWidth);
  const gapBeforeMore = row2To > row2From ? CATALOG_TAGS_INLINE_GAP_PX : 0;
  return tagsWidth + gapBeforeMore + moreWidth;
}

type LayoutCandidate = Pick<
  CatalogTagsInlineFitResult,
  'visibleCount' | 'row1Count' | 'showMore' | 'hiddenCount' | 'lastTagMaxWidth'
>;

function tryLayout(
  clientWidth: number,
  naturalWidths: number[],
  k: number,
  showMore: boolean,
  moreBadgeWidth: (hiddenCount: number) => number
): LayoutCandidate | null {
  const hiddenCount = naturalWidths.length - k;
  const wMore = showMore ? moreBadgeWidth(hiddenCount) : 0;

  for (let row1Count = k; row1Count >= 0; row1Count -= 1) {
    const row1W = segmentWidth(naturalWidths, 0, row1Count);
    if (row1W > clientWidth + 0.5) continue;

    const row2From = row1Count;
    const row2To = k;

    if (!showMore) {
      if (row2To <= row2From) {
        return {
          visibleCount: k,
          row1Count,
          showMore: false,
          hiddenCount: 0,
          lastTagMaxWidth: undefined,
        };
      }
      const row2W = segmentWidth(naturalWidths, row2From, row2To);
      if (row2W <= clientWidth + 0.5) {
        return {
          visibleCount: k,
          row1Count,
          showMore: false,
          hiddenCount: 0,
          lastTagMaxWidth: undefined,
        };
      }
      continue;
    }

    if (row2To <= row2From) {
      if (wMore <= clientWidth + 0.5) {
        return {
          visibleCount: k,
          row1Count,
          showMore: true,
          hiddenCount,
          lastTagMaxWidth: undefined,
        };
      }
      continue;
    }

    const prefixRow2 = segmentWidth(naturalWidths, row2From, row2To - 1);
    const gapsBeforeLast =
      row2To - row2From > 1 ? CATALOG_TAGS_INLINE_GAP_PX * (row2To - row2From - 1) : 0;
    const lastMax =
      clientWidth - prefixRow2 - gapsBeforeLast - CATALOG_TAGS_INLINE_GAP_PX - wMore;

    if (lastMax < CATALOG_TAGS_INLINE_MIN_LAST_TAG_MAX_WIDTH_PX) {
      continue;
    }

    const clampedLastMax = Math.max(CATALOG_TAGS_INLINE_MIN_LAST_TAG_MAX_WIDTH_PX, lastMax);
    const row2Total = row2WidthWithMore(
      naturalWidths,
      row2From,
      row2To,
      clampedLastMax,
      wMore
    );

    if (row2Total <= clientWidth + 0.5) {
      return {
        visibleCount: k,
        row1Count,
        showMore: true,
        hiddenCount,
        lastTagMaxWidth: clampedLastMax,
      };
    }
  }

  return null;
}

/**
 * Подбор видимых тегов на двух строках; «и ещё N…» — только в конце второй строки.
 */
export function computeCatalogTagsInlineFit(
  clientWidth: number,
  naturalWidths: number[],
  moreBadgeWidth: (hiddenCount: number) => number
): CatalogTagsInlineFitResult {
  const n = naturalWidths.length;
  if (n === 0) {
    return {
      visibleCount: 0,
      row1Count: 0,
      showMore: false,
      hiddenCount: 0,
      lastTagMaxWidth: undefined,
    };
  }
  if (clientWidth < 8) {
    return {
      visibleCount: 1,
      row1Count: 0,
      showMore: n > 1,
      hiddenCount: Math.max(0, n - 1),
      lastTagMaxWidth: CATALOG_TAGS_INLINE_MIN_LAST_TAG_MAX_WIDTH_PX,
    };
  }

  for (let k = n; k >= 1; k -= 1) {
    const needsMore = k < n;
    const candidate = tryLayout(clientWidth, naturalWidths, k, needsMore, moreBadgeWidth);
    if (candidate) return candidate;
  }

  const hiddenCount = Math.max(0, n - 1);
  return {
    visibleCount: 1,
    row1Count: 0,
    showMore: n > 1,
    hiddenCount,
    lastTagMaxWidth: CATALOG_TAGS_INLINE_MIN_LAST_TAG_MAX_WIDTH_PX,
  };
}
